import {inject, Inject, Injectable, signal} from "@angular/core";
import {HttpClient} from "@angular/common/http";
import {FileServiceRecordModel} from "../models/FileServiceRecord-model";
import {SearchFilterModel} from "../models/SearchFilter-model";
import {SortDirectionValuesModel} from "../models/SortDirectionValues-model";
import {CurrentPartner, PartnersService} from "./Partners.service";
import {DeleteDialogComponent} from "../helpers/delete-dialog/delete-dialog.component";
import {MatDialog} from "@angular/material/dialog";
import {DeleteFileFromDatabaseService} from "./DeleteFileFromDatabase.service";
import {NotificationService} from "./Notification.service";
import {DashboardStatisticsService} from "../dashboard-analytics/services/dashboard-statistics.service";
import {LocalStorageService} from "./LocalStorageService";
import {keepAvailableFileServiceFilters} from "../shared/constants/file-service-filter-fields";

export interface TuningDrilldown {
    tuningNames: string[];
    startDate: string;
    endDate: string;
}

@Injectable({
    providedIn: "root",
})
export class FileService {
    private url = `${this.API_URL}`;
    public fileServiceTableIsLoading = signal<boolean>(false);
    public isTableEmpty = signal<boolean>(false);

    public fileRecord = signal<FileServiceRecordModel>(
        new FileServiceRecordModel()
    );
    public displayedColumns = signal<(keyof FileServiceRecordModel)[]>([]);
    public displayedTableHeaders = signal<string[]>([]);
    public fileServiceRecords = signal<FileServiceRecordModel[]>([]);

    public pageNumber = signal<number>(0);
    private pageSize = signal<number>(50);
    public sortDirectionValues = signal<SortDirectionValuesModel>({
        active: "importid",
        direction: "desc",
    });

    public searchString = signal<string>("");
    public selectedFilters = signal<SearchFilterModel[]>([]);
    public status = signal<string >("");
    public startDate = signal<string | undefined>(undefined);
    public endDate = signal<string | undefined>(undefined);

    public openAdvanceSettings = signal<boolean>(false);
    public tuningDrilldown = signal<TuningDrilldown | null>(null);
    public tuningDrilldownHasMore = signal<boolean>(false);
    public tuningDrilldownLoading = signal<boolean>(false);
    private tuningDrilldownGeneration = 0;

    partnersService = inject(PartnersService);
    deleteService = inject(DeleteFileFromDatabaseService);
    notificationService = inject(NotificationService);
    httpClient = inject(HttpClient);
    dialog = inject(MatDialog);
    dashboardStatisticsService = inject(DashboardStatisticsService);
    private localStorageService = inject(LocalStorageService);

    constructor(
        @Inject("API_URL") private API_URL: string,
        @Inject("LOCAL_FILE_KEY") public fileServiceFilter: string
    ) {
    }

    activateTuningDrilldown(filter: TuningDrilldown): void {
        this.tuningDrilldownGeneration++;
        this.tuningDrilldown.set({...filter, tuningNames: [...filter.tuningNames]});
        this.tuningDrilldownHasMore.set(false);
        this.tuningDrilldownLoading.set(false);
        this.pageNumber.set(0);
        this.fileServiceRecords.set([]);
        this.isTableEmpty.set(false);
        this.searchString.set("");
        this.selectedFilters.set([]);
        this.openAdvanceSettings.set(false);
        this.loadTuningDrilldownPage(true);
    }

    clearTuningDrilldown(): void {
        this.tuningDrilldownGeneration++;
        this.tuningDrilldown.set(null);
        this.tuningDrilldownHasMore.set(false);
        this.tuningDrilldownLoading.set(false);
    }

    reloadTuningDrilldown(): void {
        const filter = this.tuningDrilldown();
        if (filter) this.activateTuningDrilldown(filter);
    }

    private loadTuningDrilldownPage(initial: boolean): void {
        const filter = this.tuningDrilldown();
        if (!filter || this.tuningDrilldownLoading() || (!initial && !this.tuningDrilldownHasMore())) return;

        const generation = this.tuningDrilldownGeneration;
        const nextPage = initial ? undefined : this.pageNumber() + 1;
        this.tuningDrilldownLoading.set(true);
        if (initial) this.fileServiceTableIsLoading.set(true);

        this.dashboardStatisticsService.getImportsByTuning(
            filter.tuningNames, filter.startDate, filter.endDate, nextPage
        ).subscribe({
            next: response => {
                if (generation !== this.tuningDrilldownGeneration) return;
                this.pageNumber.set(response.number);
                this.fileServiceRecords.update(records =>
                    initial ? response.content : [...records, ...response.content]
                );
                this.tuningDrilldownHasMore.set(!response.last);
                this.isTableEmpty.set(this.fileServiceRecords().length === 0);
                this.tuningDrilldownLoading.set(false);
                this.fileServiceTableIsLoading.set(false);
            },
            error: () => {
                if (generation !== this.tuningDrilldownGeneration) return;
                this.tuningDrilldownHasMore.set(false);
                this.tuningDrilldownLoading.set(false);
                this.fileServiceTableIsLoading.set(false);
                this.isTableEmpty.set(this.fileServiceRecords().length === 0);
                this.notificationService.showErrorMessage('Could not load imports for this tuning.', 'Error');
            }
        });
    }

    getSavedFilters(): SearchFilterModel[] {
        const savedFilters = this.localStorageService.getFilters(this.fileServiceFilter);
        const availableFilters = keepAvailableFileServiceFilters(savedFilters);
        if (availableFilters.length !== savedFilters.length) {
            if (availableFilters.length > 0) {
                this.localStorageService.saveFilters(this.fileServiceFilter, availableFilters);
            } else {
                this.localStorageService.clearFilters(this.fileServiceFilter);
            }
        }
        return availableFilters;
    }

    openAdvancedSearchSettings() {
        this.openAdvanceSettings.set(!this.openAdvanceSettings());
    }

    closeAdvancedSettingsModal(event: boolean) {
        this.openAdvanceSettings.set(event);
    }

    restartTableValue() {
        this.displayedColumns.set([]);
        this.displayedTableHeaders.set([]);
        this.pageNumber.set(0);
        this.partnersService.pageNumber.set(0);
        this.fileServiceRecords.set([]);
        this.searchString.set("");
    }

    getTableHeaderNames() {
        // Row fields that are NOT table columns — they only gate row visuals (e.g. the AI icon).
        const hiddenKeys = new Set(["aiStatus"]);
        let objectKeys = Object.keys(this.fileRecord());
        for (let key of objectKeys) {
            if (hiddenKeys.has(key)) {
                continue;
            }
            this.displayedColumns().push(key as keyof FileServiceRecordModel);
        }
        this.displayedTableHeaders.set([...this.displayedColumns(), "actions"]);
    }

    triggerLoadFileRecordsRequest() {
        return this.httpClient.get<FileServiceRecordModel[]>(
            `${this.url}/imports/all`,
            {
                params: {
                    pageNumber: this.pageNumber(),
                    pageSize: this.pageSize(),
                    sortAttribute: this.sortDirectionValues().active,
                    sortDirection: this.sortDirectionValues().direction,
                },
            }
        );
    }

    loadFileRecords() {
        this.fileServiceTableIsLoading.set(true);
        this.triggerLoadFileRecordsRequest().subscribe({
            next: (response) => {
                if (response['content'].length > 0) {
                    this.fileServiceRecords.set(response["content"]);
                    this.fileServiceTableIsLoading.set(false);
                } else {
                    this.isTableEmpty.set(true);
                }
            },
            error: (error) => {
                this.fileServiceTableIsLoading.set(false);
                if (error.status === 409) {
                    this.notificationService.showErrorMessage(
                        "This attribute does not exist in this table",
                        "Error"
                    );
                } else {
                    this.notificationService.showErrorMessage(
                        "Something went wrong",
                        "Error Occurred"
                    );
                }
            }
        });
    }

    triggerFileRecordsByClientNumberRequest(clientNumber: string) {
        return this.httpClient.get<FileServiceRecordModel[]>(
            `${this.url}/imports/allByClientNumber`,
            {
                params: {
                    pageNumber: this.pageNumber(),
                    pageSize: this.pageSize(),
                    sortAttribute: this.sortDirectionValues().active,
                    sortDirection: this.sortDirectionValues().direction,
                    clientNumberFilter: clientNumber,
                },
            }
        );
    }


    loadFileRecordsByClientNumber(clientNumber: string) {
        this.fileServiceTableIsLoading.set(true);
        this.triggerFileRecordsByClientNumberRequest(clientNumber).subscribe(
            (response) => {
                if (response["content"].length > 0) {
                    this.fileServiceRecords.set([...response["content"]]);
                    this.fileServiceTableIsLoading.set(false);
                } else {
                    this.isTableEmpty.set(true);
                }
            }
        );
    }

    triggerListImportsFilteredRequest(filterOptions: SearchFilterModel[]) {
        return this.httpClient.post<FileServiceRecordModel[]>(
            `${this.url}/imports/listAllImportsFiltered`,
            keepAvailableFileServiceFilters(filterOptions),
            {
                params: {
                    pageNumber: this.pageNumber(),
                    pageSize: this.pageSize(),
                    sortAttribute: this.sortDirectionValues().active,
                },
            }
        );
    }

    listImportsFiltered(filterOptions: SearchFilterModel[]) {
        this.fileServiceTableIsLoading.set(true);
        this.triggerListImportsFilteredRequest(filterOptions).subscribe(
            (response) => {
                if (response.length > 0) {
                    this.fileServiceRecords.set(response);
                    this.fileServiceTableIsLoading.set(false);
                } else {
                    this.isTableEmpty.set(true);

                }
            }
        );
    }

    getAllFileServiceRecords() {

        if (this.startDate() && this.endDate()) {
            this.getStatisticsRecordsBetweenTwoDates(
                this.startDate(),
                this.endDate(),
                this.status()
            );
        } else {
            this.loadFileRecords();
        }
    }

    getAllImportsForPartners() {
        if (this.startDate() && this.endDate()) {
            this.fileServiceTableIsLoading.set(true);
            this.partnersService
                .getPartnerStatisticsBetweenTwoDates(
                    this.startDate(),
                    this.endDate(),
                    this.status()
                )
                .subscribe((response) => {
                    if (response.length > 0) {
                        this.fileServiceRecords.set([...response]);
                        this.fileServiceTableIsLoading.set(false);
                    } else {
                        this.isTableEmpty.set(true);
                    }

                });
        } else {
            this.getPartnerImportsList();
        }
    }

    getPartnerImportsList() {
        this.fileServiceTableIsLoading.set(true);
        this.partnersService.getAllImportsForPartners().subscribe((response) => {
            if (response["content"] > 0) {
                this.fileServiceRecords.set(response["content"]);
                this.fileServiceTableIsLoading.set(false);
            } else {
                this.isTableEmpty.set(true);
            }
        });
    }


    getStatisticsRecordsBetweenTwoDatesRequest(startDate: string, endDate: string, status: string) {

    return this.httpClient.get<{content: FileServiceRecordModel[]}>(`${this.url}/statistics/statisticsRecordsBetweenTwoDatesByStatus`, {
        params: {
            'start date': startDate,
            'end date': endDate,
            'status': status,
            pageNumber: this.pageNumber(),
            pageSize: this.pageSize(),
            sortAttribute: this.sortDirectionValues().active,
            sortDirection: this.sortDirectionValues().direction,
        }
    });
}


    getStatisticsRecordsBetweenTwoDates(startDate: string, endDate: string, status: string) {
        this.fileServiceTableIsLoading.set(true);
        this.getStatisticsRecordsBetweenTwoDatesRequest(startDate, endDate, status).subscribe({
            next: response => {
                this.fileServiceRecords.set(response.content);
                this.isTableEmpty.set(response.content.length === 0);
                this.fileServiceTableIsLoading.set(false);
            },
            error: () => {
                this.fileServiceRecords.set([]);
                this.isTableEmpty.set(true);
                this.fileServiceTableIsLoading.set(false);
                this.notificationService.showErrorMessage('Could not load Imports for the selected dates.', 'Error');
            }
        });
    }

    deleteItemFromTable(id: number, tableName: string) {
        const dialogRef = this.dialog.open(DeleteDialogComponent, {
            width: "350px",
            data: "Are you sure you want to delete?",
        });

        dialogRef.afterClosed().subscribe((confirmed) => {
            if (confirmed) {
                this.deleteService.deleteFileFromDatabase(id, tableName).subscribe(
                    (response) => {
                        if (response.status === 200) {
                            this.fileServiceRecords.update((fileRecords) =>
                                fileRecords.filter((file) => +file.importid !== id)
                            );
                            this.notificationService.showSuccessMessage(
                                "Successfully deleted",
                                `${response.body["message"]}`
                            );
                        }
                    },
                    (error) => {
                        this.notificationService.showErrorMessage(
                            "Something went wrong",
                            `${error.error["message"]}`
                        );
                    }
                );
            }
        });
    }

    onScroll() {
        if (this.tuningDrilldown()) {
            this.loadTuningDrilldownPage(false);
            return;
        }

        switch (this.partnersService.currentPartner) {
            case CurrentPartner.DynoChipTuningFiles: {
                if (
                    this.searchString().length === 0 &&
                    this.selectedFilters().length == 0
                ) {
                    if (this.startDate() && this.endDate()) {
                        this.increasePageNumber();
                        this.getStatisticsRecordsBetweenTwoDatesRequest(
                            this.startDate(),
                            this.endDate(),
                            this.status()
                        ).subscribe((response) =>
                            this.fileServiceRecords.update((fileRecords) => [
                                ...fileRecords,
                                ...response["content"],
                            ])
                        );
                    } else {
                        this.increasePageNumber();
                        this.triggerLoadFileRecordsRequest().subscribe((response) => {
                            this.fileServiceRecords.update((fileRecords) => [
                                ...fileRecords,
                                ...response["content"],
                            ]);
                        });
                    }
                }
                if (
                    this.searchString().length >= 3 &&
                    this.selectedFilters().length === 0
                ) {
                    this.increasePageNumber();
                    this.triggerFileRecordsByClientNumberRequest(
                        this.searchString().trim()
                    ).subscribe((response) => {
                        this.fileServiceRecords.update((fileRecords) => [
                            ...fileRecords,
                            ...response["content"],
                        ]);
                    });
                }
                if (this.selectedFilters().length > 0) {
                    this.increasePageNumber();
                    this.triggerListImportsFilteredRequest(
                        this.selectedFilters()
                    ).subscribe((response) => {
                        this.fileServiceRecords.update((fileRecords) => [
                            ...fileRecords,
                            ...response,
                        ]);
                    });
                }
                break;
            }

            case CurrentPartner.ATMChipTunings: {
                if (!this.searchString() && this.selectedFilters().length == 0) {
                    if (this.startDate() && this.endDate()) {
                        this.partnersService.loadNewPage();
                        this.partnersService
                            .getPartnerStatisticsBetweenTwoDates(
                                this.startDate(),
                                this.endDate(),
                                this.status()
                            )
                            .subscribe((response) => {
                                this.fileServiceRecords.update((fileRecords) => [
                                    ...fileRecords,
                                    ...response,
                                ]);
                            });
                    } else {
                        this.partnersService.loadNewPage();
                        this.partnersService
                            .getAllImportsForPartners()
                            .subscribe((value) => {
                                this.fileServiceRecords.update((fileRecords) => [
                                    ...fileRecords,
                                    ...value["content"],
                                ]);
                            });
                    }
                }
                if (this.selectedFilters().length > 0) {
                    this.partnersService.loadNewPage();
                    this.partnersService
                        .getAllImportsForPartnersFiltered(this.selectedFilters())
                        .subscribe((response) => {
                            this.fileServiceRecords.update((fileRecords) => [
                                ...fileRecords,
                                ...response,
                            ]);
                        });
                }
                break;
            }
        }
    }

    changeSelectFilters(selectedFilters: SearchFilterModel[]) {
        this.selectedFilters.set(keepAvailableFileServiceFilters(selectedFilters));
        this.restartFileServiceRecords();
        if (this.selectedFilters().length === 0) {
            this.onRefreshTable();
            return;
        }
        switch (this.partnersService.currentPartner) {
            case CurrentPartner.DynoChipTuningFiles: {
                this.listImportsFiltered(this.selectedFilters());
                break;
            }

            case CurrentPartner.ATMChipTunings: {
                this.partnersService
                    .getAllImportsForPartnersFiltered(this.selectedFilters())
                    .subscribe((response) => {
                        this.fileServiceRecords.set(response);
                    });
                break;
            }
        }
    }

    private normalizeSearchInput(input: string): string {
        const trimmed = (input ?? '').trim();
        if (trimmed.toLowerCase().endsWith('.ols')) {
            return trimmed.slice(0, -4);
        }
        return trimmed;
    }

    onSearchInput(searchString: string) {
        const cleaned = this.normalizeSearchInput(searchString);
        this.searchString.set(cleaned);

        if (cleaned.length >= 3) {
            this.restartFileServiceRecords();
            this.loadFileRecordsByClientNumber(cleaned);
        } else {
            this.loadFileRecords();
        }
    }

    downloadFileFromTable(importId: number, fileName: string) {
        const toastObj = this.notificationService.showInfoMessage(
            `Preparing your ${fileName} file...`,
            "Preparing.."
        );

        switch (this.partnersService.currentPartner) {
            case CurrentPartner.DynoChipTuningFiles: {
                this.notificationService.clear(toastObj.toastId);
                this.downloadClientImport(importId, fileName);
                break;
            }
            case CurrentPartner.ATMChipTunings: {
                this.partnersService.downloadPartnerClientImport(importId, fileName);
                this.notificationService.clear(toastObj.toastId);
            }
        }
    }

    downloadClientImport(importId: number, fileName: string) {
        return this.httpClient
            .post<{ url: string; message: string }>(
                `${this.url}/imports/downloadClientImport`,
                {},
                {
                    observe: "response",
                    params: {
                        importId: importId,
                    },
                }
            )
            .subscribe((response) => {
                if (response.status === 200) {
                    window.location.href = response.body.url;
                    this.notificationService.showSuccessMessage(
                        `<div><span>Your file ${fileName} is downloaded</span></div>`,
                        "Success"
                    );
                } else {
                    this.notificationService.showErrorMessage(
                        "Something went wrong with file.",
                        "Failed"
                    );
                }
            });
    }

    restartFileServiceRecords() {
        this.pageNumber.set(0);
        this.pageSize.set(50);
        this.partnersService.pageNumber.set(0);
        this.fileServiceRecords.set([]);
    }

    onRefreshTable() {
        if (this.tuningDrilldown()) {
            this.reloadTuningDrilldown();
            return;
        }

        this.searchString.set("");
        this.fileServiceTableIsLoading.set(true);
        if (this.startDate() && this.endDate()) {
            this.restartFileServiceRecords();
            this.selectedFilters.set([]);
            switch (this.partnersService.currentPartner) {
                case CurrentPartner.DynoChipTuningFiles:
                    this.getAllFileServiceRecords();
                    break;
                case CurrentPartner.ATMChipTunings:
                    this.getAllImportsForPartners();
                    break;
            }
            return;
        }
        const savedFilters = this.getSavedFilters();
        this.selectedFilters.set(savedFilters);

        switch (this.partnersService.currentPartner) {
            case CurrentPartner.DynoChipTuningFiles: {
                this.restartFileServiceRecords();
                if (savedFilters.length > 0) {
                    this.triggerListImportsFilteredRequest(savedFilters).subscribe(response => {
                        this.fileServiceRecords.set([...response]);
                        this.fileServiceTableIsLoading.set(false);
                    });
                } else {
                    this.getAllFileServiceRecords();
                }
                break;
            }
            case CurrentPartner.ATMChipTunings: {
                this.restartFileServiceRecords();
                if (savedFilters.length > 0) {
                    this.partnersService.getAllImportsForPartnersFiltered(savedFilters).subscribe(response => {
                        this.fileServiceRecords.set([...response]);
                        this.fileServiceTableIsLoading.set(false);
                    });
                } else {
                    this.getAllImportsForPartners();
                }
                break;
            }
        }
    }

    changeFileServiceTableSortDirection(
        sortEvent: SortDirectionValuesModel,
        tableElement: HTMLElement
    ) {
        if (this.tuningDrilldown()) return;

        if (tableElement) {
            tableElement.scrollTop = 0;
        }
        const nonSortable = this.displayedColumns().slice(-3) as string[];
        if (nonSortable.includes(sortEvent.active)) {
            return;
        }
        switch (this.partnersService.currentPartner) {
            case CurrentPartner.DynoChipTuningFiles: {
                this.sortDirectionValues.set(sortEvent);
                this.pageNumber.set(0);

                const currentSearch = this.searchString().trim();

                if (currentSearch.length >= 3 && this.selectedFilters().length === 0) {
                    this.triggerFileRecordsByClientNumberRequest(currentSearch).subscribe(
                        (response) => {
                            this.fileServiceRecords.set(response["content"]);
                        }
                    );
                } else if (this.selectedFilters().length > 0) {
                    this.triggerListImportsFilteredRequest(this.selectedFilters()).subscribe(
                        (response) => {
                            this.fileServiceRecords.set(response);
                        }
                    );
                } else if (this.startDate() && this.endDate()) {
                    this.getAllFileServiceRecords();
                } else {
                    this.loadFileRecords();
                }
                break;
            }
            case CurrentPartner.ATMChipTunings: {
                this.partnersService.sortDirectionValues.set(sortEvent);
                this.partnersService.pageNumber.set(0);
                this.getPartnerImportsList();
                break;
            }
        }
    }


    increasePageNumber() {
        this.pageNumber.update((pageNum) => pageNum + 1);
    }
    resetSortDirection() {
        this.sortDirectionValues.set({
            active: 'importid',
            direction: 'desc'
        });
    }


    clearErrorState() {
        this.fileServiceTableIsLoading.set(false);
        this.isTableEmpty.set(false);
        this.notificationService.clearAll();
        this.resetSortDirection();



    }
}
